import nacl from 'tweetnacl';
import util from 'tweetnacl-util';

// Types for our key pair
export interface KeyPair {
    publicKey: string;
    privateKey: string;
}

export const STORAGE_KEY = 'ticket-chat-keys';

// Helper to encode/decode Base64
export const encodeBase64 = util.encodeBase64;
export const decodeBase64 = util.decodeBase64;

// Helper to encode/decode UTF8
export const encodeUTF8 = util.encodeUTF8;
export const decodeUTF8 = util.decodeUTF8;

/**
 * Retrieve current keypair from localStorage.
 * Returns null if no keys exist yet (user hasn't logged in or set up E2EE).
 * @param userId - Optional ID to retrieve user-specific keys. 
 */
export const getMyKeys = (userId?: string): KeyPair | null => {
    // 1. Try user-specific key first
    if (userId) {
        const userStored = localStorage.getItem(`${STORAGE_KEY}_${userId}`);
        if (userStored) {
            try {
                return JSON.parse(userStored);
            } catch (e) {
                console.error(`[Crypto] Failed to parse user keys for ${userId}`, e);
            }
        }
        return null; // Strict: if you ask for a user's keys, don't fall back to global
    }

    // 2. Fallback to legacy global key (only if NO userId provided)
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
        try {
            return JSON.parse(stored);
        } catch (e) {
            console.error('[Crypto] Failed to parse legacy keys', e);
        }
    }
    return null;
};

/**
 * Encrypt a message for multiple recipients
 * Returns the encrypted content and a map of keys for each recipient
 */
export const encryptMessage = (
    content: string,
    recipientPublicKeys: string[],
    userId?: string
): { encryptedContent: string; encryptedKeys: Record<string, string> } => {

    // 1. Generate a random ephemeral key for this message
    const messageKey = nacl.randomBytes(nacl.secretbox.keyLength);
    const messageNonce = nacl.randomBytes(nacl.secretbox.nonceLength);

    // 2. Encrypt the actual content with this symmetric key
    const messageBytes = decodeUTF8(content);

    const encryptedContentBlob = nacl.secretbox(
        messageBytes,
        messageNonce,
        messageKey
    );

    // Pack nonce + content together
    const fullEncryptedContent = new Uint8Array(messageNonce.length + encryptedContentBlob.length);
    fullEncryptedContent.set(messageNonce);
    fullEncryptedContent.set(encryptedContentBlob, messageNonce.length);

    // 3. Encrypt the symmetric key for each recipient using their public key
    const encryptedKeys: Record<string, string> = {};
    const myKeys = getMyKeys(userId);
    if (!myKeys) throw new Error('E2EE keys not initialized. Please log out and log back in.');
    const myMsgSecretKey = decodeBase64(myKeys.privateKey);

    recipientPublicKeys.forEach(pubKeyStr => {
        if (!pubKeyStr) return;

        const recipientPubKey = decodeBase64(pubKeyStr);
        const nonce = nacl.randomBytes(nacl.box.nonceLength);

        // Encrypt the messageKey for this recipient
        const encryptedKeyBlob = nacl.box(
            messageKey,
            nonce,
            recipientPubKey,
            myMsgSecretKey
        );

        // Pack nonce + encrypted key
        const fullEncryptedKey = new Uint8Array(nonce.length + encryptedKeyBlob.length);
        fullEncryptedKey.set(nonce);
        fullEncryptedKey.set(encryptedKeyBlob, nonce.length);

        encryptedKeys[pubKeyStr] = encodeBase64(fullEncryptedKey);
    });

    return {
        encryptedContent: encodeBase64(fullEncryptedContent),
        encryptedKeys
    };
};

/**
 * Decrypt a message
 */
export const decryptMessage = (
    encryptedContentStr: string,
    encryptedKeyStr: string,
    senderPublicKeyStr: string,
    userId?: string
): string | null => {
    try {
        const myKeys = getMyKeys(userId);
        if (!myKeys) return null; // No local keys — user needs to log in
        const mySecretKey = decodeBase64(myKeys.privateKey);
        const senderPublicKey = decodeBase64(senderPublicKeyStr);

        // 1. Decrypt the symmetric message key first
        const fullEncryptedKey = decodeBase64(encryptedKeyStr);
        const keyNonce = fullEncryptedKey.slice(0, nacl.box.nonceLength);
        const keyCiphertext = fullEncryptedKey.slice(nacl.box.nonceLength);

        const messageKey = nacl.box.open(
            keyCiphertext,
            keyNonce,
            senderPublicKey,
            mySecretKey
        );

        if (!messageKey) return null;

        // 2. Decrypt the content using the message key
        const fullEncryptedContent = decodeBase64(encryptedContentStr);
        const msgNonce = fullEncryptedContent.slice(0, nacl.secretbox.nonceLength);
        const msgCiphertext = fullEncryptedContent.slice(nacl.secretbox.nonceLength);

        const decryptedContent = nacl.secretbox.open(
            msgCiphertext,
            msgNonce,
            messageKey
        );

        if (!decryptedContent) return null;

        return encodeUTF8(decryptedContent);
    } catch (e) {
        console.error('Decryption failed:', e);
        return null;
    }
};

/**
 * Derive a wrapping key from password and salt using PBKDF2
 */
export const deriveKeyFromPassword = async (password: string, salt: string): Promise<CryptoKey> => {
    const enc = new TextEncoder();
    const passwordKey = await window.crypto.subtle.importKey(
        "raw",
        enc.encode(password),
        { name: "PBKDF2" },
        false,
        ["deriveKey"]
    );

    return window.crypto.subtle.deriveKey(
        {
            name: "PBKDF2",
            salt: decodeBase64(salt).buffer as ArrayBuffer,
            iterations: 100000,
            hash: "SHA-256"
        },
        passwordKey,
        { name: "AES-GCM", length: 256 },
        true,
        ["encrypt", "decrypt"]
    );
};

/**
 * Encrypt the private key with the derived wrapping key
 */
export const encryptPrivateKey = async (privateKey: string, wrappingKey: CryptoKey): Promise<string> => {
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const enc = new TextEncoder();

    // Force strict type bypass
    const params: any = {
        name: "AES-GCM",
        iv: iv
    };

    const encryptedContent = await window.crypto.subtle.encrypt(
        params,
        wrappingKey,
        enc.encode(privateKey)
    );

    const encryptedBytes = new Uint8Array(encryptedContent);
    const fullBytes = new Uint8Array(iv.length + encryptedBytes.length);
    fullBytes.set(iv);
    fullBytes.set(encryptedBytes, iv.length);

    return encodeBase64(fullBytes);
};

/**
 * Decrypt the private key with the derived wrapping key
 */
export const decryptPrivateKey = async (encryptedPrivateKey: string, wrappingKey: CryptoKey): Promise<string | null> => {
    try {
        const fullBytes = decodeBase64(encryptedPrivateKey);
        const iv = fullBytes.slice(0, 12);
        const data = fullBytes.slice(12);

        // Force strict type bypass
        const params: any = {
            name: "AES-GCM",
            iv: iv
        };

        const decryptedContent = await window.crypto.subtle.decrypt(
            params,
            wrappingKey,
            data
        );

        const dec = new TextDecoder();
        return dec.decode(decryptedContent);
    } catch (e) {
        console.error('Failed to decrypt private key:', e);
        return null;
    }
};

/**
 * Generate a random salt for key derivation
 */
export const generateSalt = (): string => {
    const salt = window.crypto.getRandomValues(new Uint8Array(16));
    return encodeBase64(salt);
};

/**
 * Derive a wrapping key from an OAuth `sub` claim (stable unique user ID per provider).
 * Uses the same PBKDF2 → AES-GCM path as deriveKeyFromPassword so encryptPrivateKey
 * and decryptPrivateKey work with it directly.
 *
 * @param sub       The `sub` field from the decoded OAuth ID token
 * @param provider  'google' | 'microsoft' — prevents cross-provider key reuse
 * @param salt      Base64-encoded random salt (generated at link time, stored server-side)
 */
export const deriveKeyFromOAuthSub = async (
    sub: string,
    provider: string,
    salt: string
): Promise<CryptoKey> => {
    const material = `${sub}:${provider}`;
    const enc = new TextEncoder();
    const passwordKey = await window.crypto.subtle.importKey(
        'raw',
        enc.encode(material),
        { name: 'PBKDF2' },
        false,
        ['deriveKey']
    );
    return window.crypto.subtle.deriveKey(
        {
            name: 'PBKDF2',
            salt: decodeBase64(salt).buffer as ArrayBuffer,
            iterations: 100000,
            hash: 'SHA-256',
        },
        passwordKey,
        { name: 'AES-GCM', length: 256 },
        true,
        ['encrypt', 'decrypt']
    );
};

/**
 * Generate an escrow (admin recovery) key for the given private key.
 * 
 * Returns:
 *   - recoveryKey: a human-readable hex string shown to the user / emailed to admin
 *   - encPrivateKeyEscrow: the private key encrypted with the recovery key (stored on server)
 * 
 * The recoveryKey is NEVER stored on the server — only the encrypted blob is.
 */
export const generateEscrowKey = async (privateKey: string): Promise<{ recoveryKey: string; encPrivateKeyEscrow: string }> => {
    // 1. Generate a random 32-byte recovery key
    const rawKey = window.crypto.getRandomValues(new Uint8Array(32));

    // 2. Convert to a readable hex string (e.g. "a3f2...") for display / email
    const recoveryKey = Array.from(rawKey).map(b => b.toString(16).padStart(2, '0')).join('');

    // 3. Import the raw bytes as an AES-GCM CryptoKey
    const cryptoKey = await window.crypto.subtle.importKey(
        'raw',
        rawKey,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
    );

    // 4. Encrypt the private key with this recovery key (reuse existing helper)
    const encPrivateKeyEscrow = await encryptPrivateKey(privateKey, cryptoKey);

    return { recoveryKey, encPrivateKeyEscrow };
};
